/**
 * "Compare plans" (A sheet, also F3 price help). Read-only: it can never
 * purchase and it never changes the host's selection; "Back to my choice"
 * just closes it.
 */
import { useTranslation } from 'react-i18next';
import type { PlanTier } from '@tabbio/types';
import { useSubscriptionState } from '../../hooks/useSubscriptionState';
import {
  CHOOSER_TIERS,
  COMPARE_FEATURE_KEYS,
  modelInterpolation,
} from '../../lib/subscription-journey/plan-presentation';
import { getTierPrices } from '../../lib/subscription-journey/pricing';
import { JourneyDialog, SubButton, SubList, TierMark } from './journey-ui.web';

const TIER_NAMES: Record<PlanTier, string | null> = {
  free: null,
  plus: 'Plus',
  pro: 'Pro',
};

export function ComparePlansDialog({
  onClose,
  zIndex = 1200,
}: {
  onClose: () => void;
  zIndex?: number;
}) {
  const { t } = useTranslation('common');
  const { catalog } = useSubscriptionState();
  return (
    <JourneyDialog
      title={t('subscription.compare.title')}
      onClose={onClose}
      zIndex={zIndex}
      maxWidth={480}
      footer={
        <SubButton kind="secondary" onClick={onClose}>
          {t('subscription.compare.backToChoice')}
        </SubButton>
      }
    >
      <div className="tabbio-sub-plan-compare">
        {CHOOSER_TIERS.map((tier) => {
          const prices = getTierPrices(catalog, 'individual', tier);
          const options = tier === 'free' ? {} : modelInterpolation(tier);
          return (
            <section key={tier}>
              <h3>
                {TIER_NAMES[tier] ?? t('subscription.tierFree')}
                <TierMark tier={tier} size={18} />
                <span className="tabbio-sub-muted">
                  {tier === 'free' || !prices
                    ? t('subscription.tierFree')
                    : t('subscription.compare.pricePerMonth', {
                        price: prices.monthlyDisplay,
                      })}
                </span>
              </h3>
              <SubList
                items={COMPARE_FEATURE_KEYS[tier].map((key) => t(key, options))}
              />
            </section>
          );
        })}
      </div>
    </JourneyDialog>
  );
}
